import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { FileSpreadsheet, Upload, Loader2, Check, AlertCircle, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryExcelUploader = () => {
  const { hasRole } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mapping, setMapping] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ newCount: 0, existingCount: 0 });
  const [mode, setMode] = useState('upsert');
  const [importLoading, setImportLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  if (!hasRole('admin')) {
    return null; // Admin-only feature
  }

  const handleScan = async (e) => {
    e.preventDefault();
    if (!file) return;
    setError('');
    setLoading(true);
    setResult(null);
    setItems([]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/ai/scan-inventory-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMapping(data.mapping);
      setItems(data.items);
      setStats({ newCount: data.newCount, existingCount: data.existingCount });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to scan Excel file');
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (idx, field, value) => {
    const updated = [...items];
    if (field === 'price') updated[idx][field] = parseFloat(value) || 0;
    else if (field === 'stockQuantity' || field === 'reorderLevel') updated[idx][field] = parseInt(value) || 0;
    else updated[idx][field] = value;
    setItems(updated);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleImport = async () => {
    setImportLoading(true);
    setError('');
    try {
      const payload = {
        items: items.map(({ name, price, stockQuantity, reorderLevel }) => ({
          name, price, stockQuantity, reorderLevel,
        })),
        mode,
      };
      const { data } = await axios.post(`${API}/medicines/bulk-import`, payload);
      setResult(data);
      setItems([]);
      setFile(null);
      setMapping(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setItems([]);
    setFile(null);
    setMapping(null);
    setError('');
  };

  return (
    <div className="card p-6 mb-8" data-testid="inventory-excel-uploader">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl" style={{ backgroundColor: '#F5E6F5' }}>
          <FileSpreadsheet size={28} style={{ color: '#990099' }} />
        </div>
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            Inventory Excel Import
            <span
              className="ml-3 px-2 py-0.5 rounded-full text-xs font-medium uppercase tracking-wide align-middle"
              style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
            >
              Admin Only
            </span>
          </h2>
          <p className="text-sm" style={{ color: '#6B7280' }}>
            Upload an Excel or CSV file — AI maps columns to inventory fields automatically
          </p>
        </div>
      </div>

      {/* Step 1: Upload */}
      {items.length === 0 && !result && (
        <form onSubmit={handleScan}>
          <div
            className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer"
            style={{ borderColor: file ? '#990099' : '#E5E0D8', backgroundColor: file ? '#F5E6F5' : 'transparent' }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              setFile(e.dataTransfer.files[0]);
            }}
            onClick={() => document.getElementById('inventory-excel-input').click()}
          >
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
              id="inventory-excel-input"
              data-testid="inventory-excel-input"
            />
            <FileSpreadsheet size={48} style={{ color: '#990099', margin: '0 auto' }} />
            <p className="mt-4 font-medium" style={{ color: '#2D3A2A' }}>
              {file ? file.name : 'Click to upload Excel/CSV file'}
            </p>
            <p className="text-sm mt-2" style={{ color: '#6B7280' }}>
              Supports .xlsx, .xls, .csv — AI will auto-detect columns like name, price, stock, reorder level
            </p>
          </div>

          {error && (
            <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }} data-testid="excel-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="primary-btn w-full mt-6 flex items-center justify-center gap-2"
            disabled={loading || !file}
            style={{ opacity: loading || !file ? 0.5 : 1 }}
            data-testid="scan-excel-btn"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                AI is analyzing columns...
              </>
            ) : (
              <>
                <Sparkles size={20} />
                Scan & Map Columns
              </>
            )}
          </button>
        </form>
      )}

      {/* Step 2: Preview & Edit */}
      {items.length > 0 && !result && (
        <div data-testid="inventory-preview">
          {/* Mapping summary */}
          <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#F5E6F5' }}>
            <p className="text-xs uppercase tracking-wide font-medium mb-2" style={{ color: '#990099' }}>
              AI-Detected Column Mapping
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span style={{ color: '#6B7280' }}>Name → </span>
                <strong style={{ color: '#2D3A2A' }}>{mapping?.name || '—'}</strong>
              </div>
              <div>
                <span style={{ color: '#6B7280' }}>Price → </span>
                <strong style={{ color: '#2D3A2A' }}>{mapping?.price || '—'}</strong>
              </div>
              <div>
                <span style={{ color: '#6B7280' }}>Stock → </span>
                <strong style={{ color: '#2D3A2A' }}>{mapping?.stockQuantity || '—'}</strong>
              </div>
              <div>
                <span style={{ color: '#6B7280' }}>Reorder → </span>
                <strong style={{ color: '#2D3A2A' }}>{mapping?.reorderLevel || '—'}</strong>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#F9F6F0' }}>
              <p className="text-2xl font-bold" style={{ color: '#2D3A2A' }} data-testid="total-rows">{items.length}</p>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#6B7280' }}>Total Rows</p>
            </div>
            <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#DBEAFE' }}>
              <p className="text-2xl font-bold" style={{ color: '#1E40AF' }} data-testid="new-count">{stats.newCount}</p>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#1E40AF' }}>New</p>
            </div>
            <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#FEF3C7' }}>
              <p className="text-2xl font-bold" style={{ color: '#92400E' }} data-testid="existing-count">{stats.existingCount}</p>
              <p className="text-xs uppercase tracking-wide" style={{ color: '#92400E' }}>Already Exists</p>
            </div>
          </div>

          {/* Editable table */}
          <div className="overflow-x-auto mb-4">
            <table data-testid="inventory-preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Medicine Name</th>
                  <th>Price (₹)</th>
                  <th>Stock</th>
                  <th>Reorder Level</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={`${item.name}-${idx}`} data-testid={`inventory-row-${idx}`}>
                    <td style={{ color: '#6B7280' }}>{idx + 1}</td>
                    <td>
                      <input type="text" value={item.name} onChange={(e) => updateItem(idx, 'name', e.target.value)} data-testid={`inv-name-${idx}`} />
                    </td>
                    <td>
                      <input type="number" step="0.01" value={item.price} onChange={(e) => updateItem(idx, 'price', e.target.value)} data-testid={`inv-price-${idx}`} />
                    </td>
                    <td>
                      <input type="number" value={item.stockQuantity} onChange={(e) => updateItem(idx, 'stockQuantity', e.target.value)} data-testid={`inv-stock-${idx}`} />
                    </td>
                    <td>
                      <input type="number" value={item.reorderLevel} onChange={(e) => updateItem(idx, 'reorderLevel', e.target.value)} data-testid={`inv-reorder-${idx}`} />
                    </td>
                    <td>
                      {item.isExisting ? (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>Exists</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>New</span>
                      )}
                    </td>
                    <td>
                      <button onClick={() => removeItem(idx)} className="p-2 rounded hover:bg-red-50" data-testid={`inv-remove-${idx}`}>
                        <AlertCircle size={16} style={{ color: '#EF4444' }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mode toggle */}
          <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#F9F6F0' }}>
            <p className="text-xs uppercase tracking-wide font-medium mb-2" style={{ color: '#78716C' }}>Behavior for Existing Items</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="radio" name="mode" value="upsert" checked={mode === 'upsert'} onChange={() => setMode('upsert')} className="w-auto" data-testid="mode-upsert" />
                <span style={{ color: '#2D3A2A' }}>Update existing (overwrite price/stock)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="radio" name="mode" value="create-only" checked={mode === 'create-only'} onChange={() => setMode('create-only')} className="w-auto" data-testid="mode-create-only" />
                <span style={{ color: '#2D3A2A' }}>Skip existing (only add new)</span>
              </label>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>{error}</div>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleImport}
              className="primary-btn flex items-center gap-2 px-6"
              disabled={importLoading || items.length === 0}
              style={{ opacity: importLoading ? 0.5 : 1 }}
              data-testid="import-inventory-btn"
            >
              {importLoading ? (
                <><Loader2 className="animate-spin" size={20} /> Importing...</>
              ) : (
                <><Check size={20} /> Import {items.length} Items</>
              )}
            </button>
            <button onClick={handleReset} className="secondary-btn" data-testid="cancel-import-btn">Cancel</button>
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {result && (
        <div className="p-6 rounded-xl text-center" style={{ backgroundColor: '#D1FAE5' }} data-testid="import-result">
          <Check size={48} style={{ color: '#065F46', margin: '0 auto' }} />
          <h3 className="text-xl font-bold mt-4" style={{ color: '#065F46' }}>Inventory Imported Successfully!</h3>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <span>Created: <strong>{result.created}</strong></span>
            <span>Updated: <strong>{result.updated}</strong></span>
            <span>Skipped: <strong>{result.skipped}</strong></span>
          </div>
          <button onClick={handleReset} className="primary-btn mt-6" data-testid="upload-another-btn">Upload Another File</button>
        </div>
      )}
    </div>
  );
};

export default InventoryExcelUploader;
